import { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { CalendarIcon, X } from "lucide-react";
import { format } from "date-fns";
import { fr as frLocale } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import type { DateRange } from "react-day-picker";

interface ChatDatePickerProps {
  lang: "fr" | "en";
  onConfirm: (formatted: string) => void;
  onDismiss: () => void;
}

const ChatDatePicker = ({ lang, onConfirm, onDismiss }: ChatDatePickerProps) => {
  const isMobile = useIsMobile();
  const [range, setRange] = useState<DateRange | undefined>();
  const today = new Date();

  const handleConfirm = () => {
    if (!range?.from) return;
    const locale = lang === "fr" ? frLocale : undefined;
    const fromStr = format(range.from, "d MMM yyyy", { locale });
    const toStr = range.to ? format(range.to, "d MMM yyyy", { locale }) : fromStr;
    const msg = lang === "fr"
      ? `Du ${fromStr} au ${toStr}`
      : `From ${fromStr} to ${toStr}`;
    onConfirm(msg);
  };

  const content = (
    <div className="flex flex-col items-center gap-4">
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        <CalendarIcon className="w-4 h-4 text-primary" />
        {lang === "fr" ? "Sélectionne tes dates de voyage" : "Select your travel dates"}
      </div>

      <Calendar
        mode="range"
        selected={range}
        onSelect={setRange}
        numberOfMonths={isMobile ? 1 : 2}
        disabled={{ before: today }}
        defaultMonth={today}
        className={cn("p-3 pointer-events-auto")}
      />

      {range?.from && (
        <p className="text-xs text-muted-foreground text-center">
          {format(range.from, "d MMM yyyy", { locale: lang === "fr" ? frLocale : undefined })}
          {range.to && ` → ${format(range.to, "d MMM yyyy", { locale: lang === "fr" ? frLocale : undefined })}`}
        </p>
      )}

      <div className="flex gap-2 w-full max-w-xs">
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={onDismiss}
        >
          <X className="w-3.5 h-3.5 mr-1" />
          {lang === "fr" ? "Annuler" : "Cancel"}
        </Button>
        <Button
          size="sm"
          className="flex-1"
          disabled={!range?.from}
          onClick={handleConfirm}
        >
          <CalendarIcon className="w-3.5 h-3.5 mr-1" />
          {lang === "fr" ? "Valider les dates" : "Confirm dates"}
        </Button>
      </div>
    </div>
  );

  // Mobile: bottom sheet style fullscreen overlay
  if (isMobile) {
    return (
      <div className="fixed inset-0 z-[60] flex items-end justify-center bg-foreground/30 backdrop-blur-sm animate-in fade-in duration-200">
        <div className="w-full max-h-[85vh] bg-background rounded-t-3xl border-t border-border/30 shadow-2xl p-5 pb-8 overflow-y-auto animate-in slide-in-from-bottom duration-300">
          {content}
        </div>
      </div>
    );
  }

  // Desktop: centered modal
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground/30 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-background rounded-2xl border border-border/30 shadow-2xl p-6 max-w-lg w-full mx-4 animate-in zoom-in-95 duration-200">
        {content}
      </div>
    </div>
  );
};

export default ChatDatePicker;
